import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/resources';

const AuthContext = createContext(null);

function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('bcaex_token'));
  const [user, setUser] = useState(() => readJSON('bcaex_user'));
  const [permissions, setPermissions] = useState(() => readJSON('bcaex_perms') || []);

  const persist = (tok, usr, perms) => {
    if (tok) localStorage.setItem('bcaex_token', tok);
    localStorage.setItem('bcaex_user', JSON.stringify(usr));
    localStorage.setItem('bcaex_perms', JSON.stringify(perms));
  };

  const login = useCallback(async (username, password) => {
    const data = await authService.login(username, password);
    persist(data.token, data.user, data.permissions);
    setToken(data.token);
    setUser(data.user);
    setPermissions(data.permissions);
    return data;
  }, []);

  const logout = useCallback(() => {
    ['bcaex_token', 'bcaex_user', 'bcaex_perms'].forEach((k) => localStorage.removeItem(k));
    setToken(null);
    setUser(null);
    setPermissions([]);
  }, []);

  // Refresh permissions from the server on load so changes apply without re-login.
  useEffect(() => {
    if (!token) return;
    authService
      .me()
      .then((data) => {
        setPermissions(data.permissions);
        setUser((prev) => ({ ...prev, ...data.user }));
        localStorage.setItem('bcaex_perms', JSON.stringify(data.permissions));
      })
      .catch(() => {
        /* 401 handled by the axios interceptor */
      });
  }, [token]);

  // Permission helper used across the UI (show/hide). Server re-checks anyway.
  const can = useCallback(
    (formCode, action = 'CanView') => {
      const p = permissions.find((x) => x.FormCode === formCode);
      return !!(p && p[action]);
    },
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{ token, user, permissions, isAuthenticated: !!token, login, logout, can }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
